import React from 'react';

const Industries = () => {
  const mainIndustries = [
    {
      id: 1,
      title: "Education",
      description: "Adapt Technology Solutions delivers cloud-ready and cost-effective education management systems supporting student registration, learning platforms, HR/Payroll and finance systems.",
      icon: "fas fa-graduation-cap",
      gradient: "gradient-blue",
      features: [
        "Improve infrastructure utilization",
        "Enhanced student performance tracking",
        "Secure, scalable digital platforms"
      ]
    },
    {
      id: 2,
      title: "Manufacturing & Industrial",
      description: "We provide specialized software and advisory services for manufacturing, agri-processing, utilities and heavy industries.",
      icon: "fas fa-industry",
      gradient: "gradient-green",
      features: [
        "Procurement coordination",
        "Inventory & workforce management",
        "Operational risk management",
        "ERP integration"
      ]
    },
    {
      id: 3,
      title: "Finance & Professional Services",
      description: "We support auditing, accounting and financial service firms with secure financial reporting platforms, practice management tools and compliance-driven systems.",
      icon: "fas fa-chart-line",
      gradient: "bg-gradient-to-r from-logo-darkblue to-logo-lightblue",
      features: [
        "Enhanced financial accuracy",
        "Regulatory compliance solutions",
        "Operational productivity tools"
      ]
    },
    {
      id: 4,
      title: "Telecommunications",
      description: "We deliver comprehensive IT solutions for telecom operators including network infrastructure management, customer experience platforms, billing systems integration, and 5G readiness assessments.",
      icon: "fas fa-satellite-dish",
      gradient: "bg-gradient-to-r from-logo-lightblue to-logo-teal",
      features: [
        "Network infrastructure management",
        "Customer experience platforms",
        "Billing systems integration",
        "5G readiness assessments"
      ]
    },
    {
      id: 5,
      title: "Healthcare",
      description: "Our healthcare technology solutions include electronic health records (EHR) integration, HIPAA-compliant systems, telemedicine platforms, and healthcare analytics for improved patient outcomes.",
      icon: "fas fa-hospital",
      gradient: "bg-gradient-to-r from-logo-teal to-logo-green3",
      features: [
        "EHR integration",
        "HIPAA-compliant systems",
        "Telemedicine platforms",
        "Healthcare analytics"
      ]
    },
    {
      id: 6,
      title: "Long-Term Insurance",
      description: "We provide specialized technology solutions for the long-term insurance industry including policy administration systems, claims processing automation, regulatory compliance (POPIA/FAIS), and actuarial modeling platforms.",
      icon: "fas fa-shield-alt",
      gradient: "bg-gradient-to-r from-logo-darkblue to-logo-teal",
      features: [
        "Policy administration systems",
        "Claims processing automation",
        "Regulatory compliance (POPIA/FAIS)",
        "Actuarial modeling platforms"
      ]
    }
  ];

  const otherIndustries = [
    {
      id: 1,
      title: "Telecommunications",
      icon: "fas fa-satellite-dish",
      color: "text-logo-lightblue"
    },
    {
      id: 2,
      title: "Hospitality",
      icon: "fas fa-concierge-bell",
      color: "text-logo-green2"
    },
    {
      id: 3,
      title: "Security Services",
      icon: "fas fa-user-shield",
      color: "text-logo-teal"
    },
    {
      id: 4,
      title: "Public Sector",
      icon: "fas fa-landmark",
      color: "text-logo-darkblue"
    },
    {
      id: 5,
      title: "Healthcare",
      icon: "fas fa-hospital",
      color: "text-logo-green3"
    },
    {
      id: 6,
      title: "Long-Term Insurance",
      icon: "fas fa-shield-alt",
      color: "text-logo-darkblue"
    }
  ];

  const scrollToContact = () => {
    const element = document.getElementById('contact');
    if (element) {
      const offset = 80;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;
      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  };

  return (
    <section id="industries" className="py-16 sm:py-20 bg-gradient-to-b from-blue-50 to-green-50">
      <div className="container mx-auto px-4 sm:px-6">
        <div className="text-center mb-12 sm:mb-16">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold gradient-text mb-4">Industries We Serve</h2>
          <p className="text-gray-600 max-w-3xl mx-auto text-base sm:text-lg">
            We deliver specialized technology solutions across diverse sectors, understanding each industry's unique challenges and opportunities.
          </p>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8 mb-8 sm:mb-12">
          {mainIndustries.map((industry) => (
            <div key={industry.id} className="bg-white industry-card p-6 sm:p-8 rounded-xl shadow-md border border-gray-100">
              <div className={`w-12 h-12 sm:w-14 sm:h-14 ${industry.gradient} rounded-lg flex items-center justify-center mb-4 sm:mb-6`}>
                <i className={`${industry.icon} text-white text-xl sm:text-2xl`}></i>
              </div>
              <h3 className="text-lg sm:text-xl font-bold text-logo-navy mb-3 sm:mb-4">{industry.title}</h3>
              <p className="text-gray-600 text-sm sm:text-base mb-4">{industry.description}</p>
              <ul className="text-gray-600 text-xs sm:text-sm space-y-1">
                {industry.features.map((feature, index) => (
                  <li key={index} className="flex items-start">
                    <i className={`fas fa-check ${index === 0 ? 'text-logo-green' : index === 1 ? 'text-logo-green2' : index === 2 ? 'text-logo-teal' : 'text-logo-darkblue'} mr-2 mt-1`}></i>
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
        
        {/* Multi-Industry Solutions */}
        <div className="bg-gradient-to-r from-blue-50 to-green-50 rounded-xl sm:rounded-2xl p-6 sm:p-8 md:p-12 border border-logo-lightblue/20">
          <div className="flex flex-col lg:flex-row items-center">
            <div className="lg:w-2/3">
              <h3 className="text-xl sm:text-2xl font-bold text-logo-navy mb-3 sm:mb-4">Multi-Industry Solutions</h3>
              <p className="text-gray-600 text-sm sm:text-base mb-6">
                We also serve telecommunications, hospitality & consumer markets, security & services, public sector, healthcare, and long-term insurance organizations. These sectors benefit from our tailored digital transformation strategies, cloud infrastructure services, cybersecurity frameworks, and enterprise systems integration.
              </p>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 sm:gap-4">
                {otherIndustries.map((industry) => (
                  <div key={industry.id} className="bg-white rounded-lg p-3 sm:p-4 text-center shadow-sm border border-gray-100 hover:border-logo-lightblue transition duration-300">
                    <i className={`${industry.icon} ${industry.color} text-xl sm:text-2xl mb-2`}></i>
                    <p className="font-medium text-logo-navy text-sm sm:text-base">{industry.title}</p>
                  </div>
                ))}
              </div>
            </div>
            <div className="lg:w-1/3 lg:pl-8 mt-6 lg:mt-0">
              <div className="bg-white p-4 sm:p-6 rounded-xl shadow-md border border-gray-100">
                <h4 className="text-base sm:text-lg font-bold text-logo-navy mb-3 sm:mb-4">Industry Expertise</h4>
                <p className="text-gray-600 text-xs sm:text-sm mb-4">
                  Each industry requires specialized knowledge. Our consultants have deep sector-specific experience to deliver relevant solutions.
                </p>
                <button 
                  onClick={scrollToContact}
                  className="btn-primary font-medium py-2 px-4 sm:px-6 rounded-lg text-center block w-full text-sm sm:text-base"
                >
                  Discuss Your Industry Needs
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Industries;